import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, Settings2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { apiRequest } from "@/lib/queryClient";
import { invalidateOfferSheetCaches } from "@/lib/offer-sheet-cache";
import { useToast } from "@/hooks/use-toast";
import type { OfferSheet } from "@shared/schema";

type SaveTarget = "user" | "client";

interface OfferSaveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  content: string;
  agencyClient?: { id: string; name: string } | null;
  onManageSheets: () => void;
}

function deriveTitle(content: string, fallback: string): string {
  const firstLine = content.split("\n")[0]?.trim() || "";
  if (firstLine.length > 0 && firstLine.length < 100) {
    return firstLine.replace(/^#+\s*/, "");
  }
  return fallback;
}

export function OfferSaveDialog({
  open,
  onOpenChange,
  content,
  agencyClient,
  onManageSheets,
}: OfferSaveDialogProps) {
  const { t } = useTranslation("chatInterface");
  const { t: tModal } = useTranslation("offerSheetModal");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: activeOfferSheet } = useQuery<OfferSheet | null>({
    queryKey: ["/api/offer-sheets/active"],
    enabled: open,
  });

  const fallbackTitle = t("saveOffer.defaultTitle", {
    date: new Date().toLocaleDateString("pl-PL"),
  });
  const [title, setTitle] = useState<string>("");
  const [setAsDefault, setSetAsDefault] = useState<boolean>(false);
  const [target, setTarget] = useState<SaveTarget>(agencyClient ? "client" : "user");

  // Reset form when dialog opens
  useEffect(() => {
    if (!open) return;
    setTitle(deriveTitle(content, fallbackTitle));
    setTarget(agencyClient ? "client" : "user");
    // If user has no active sheet yet, default the checkbox to ON (best-guess intent)
    setSetAsDefault(!activeOfferSheet);
  }, [open, content, agencyClient, activeOfferSheet, fallbackTitle]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const trimmed = title.trim() || fallbackTitle;

      if (target === "client" && agencyClient) {
        await apiRequest("PUT", `/api/my-clients/${agencyClient.id}/offer-sheet`, {
          title: trimmed,
          content,
        });
        return { mode: "client" as const };
      }

      const created = (await apiRequest("POST", "/api/offer-sheets", {
        title: trimmed,
        content,
      })) as { id: string };

      if (setAsDefault && created?.id) {
        await apiRequest("PUT", `/api/offer-sheets/${created.id}/activate`);
      }
      return { mode: "user" as const, activated: setAsDefault };
    },
    onSuccess: (result) => {
      // VP-94: odśwież KOMPLET pickerów arkusza (lista solo, picker przy kliencie,
      // podgląd arkusza klienta) — inaczej świeżo zapisany arkusz nie pojawia się na liście.
      invalidateOfferSheetCaches(
        queryClient,
        result.mode === "client" ? agencyClient?.id : undefined
      );

      const description =
        result.mode === "client"
          ? t("saveOffer.successDescClient", { name: agencyClient?.name })
          : result.activated
            ? t("saveOffer.successDescDefault")
            : t("saveOffer.successDesc");

      toast({ title: t("saveOffer.success"), description });
      onOpenChange(false);
    },
    onError: () => {
      toast({
        title: t("saveOffer.tryAgain"),
        description: t("saveOffer.retryDesc"),
        variant: "destructive",
      });
    },
  });

  const isClientSave = target === "client" && !!agencyClient;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("saveOffer.dialogTitle")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="offer-save-title">{t("saveOffer.titleLabel")}</Label>
            <Input
              id="offer-save-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={fallbackTitle}
              maxLength={100}
            />
          </div>

          {agencyClient && (
            <div className="space-y-2">
              <Label>{t("saveOffer.targetLabel")}</Label>
              <RadioGroup
                value={target}
                onValueChange={(v) => setTarget(v as SaveTarget)}
                className="space-y-2"
              >
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value="client" id="target-client" className="mt-1" />
                  <Label
                    htmlFor="target-client"
                    className="font-normal cursor-pointer leading-tight"
                  >
                    {t("saveOffer.targetClient", { name: agencyClient.name })}
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t("saveOffer.targetClientHint")}
                    </p>
                  </Label>
                </div>
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value="user" id="target-user" className="mt-1" />
                  <Label htmlFor="target-user" className="font-normal cursor-pointer leading-tight">
                    {t("saveOffer.targetUser")}
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t("saveOffer.targetUserHint")}
                    </p>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {!isClientSave && (
            <div className="flex items-start space-x-2">
              <Checkbox
                id="offer-save-default"
                checked={setAsDefault}
                onCheckedChange={(checked) => setSetAsDefault(checked === true)}
                className="mt-0.5"
              />
              <Label
                htmlFor="offer-save-default"
                className="font-normal cursor-pointer leading-tight"
              >
                {t("saveOffer.setAsDefaultLabel")}
                <p className="text-xs text-muted-foreground mt-0.5">
                  {activeOfferSheet
                    ? t("saveOffer.setAsDefaultHintReplace", { current: activeOfferSheet.title })
                    : t("saveOffer.setAsDefaultHintFirst")}
                </p>
              </Label>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={saveMutation.isPending}
            >
              {tModal("cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              {saveMutation.isPending ? t("saveOffer.saving") : t("saveOffer.saveButton")}
            </Button>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              onOpenChange(false);
              onManageSheets();
            }}
            className="gap-2 text-xs w-full justify-center"
          >
            <Settings2 className="h-3.5 w-3.5" />
            {t("messages.manageOfferSheets")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
